// Site context stores data for the session (cleared on refresh)

import React, { useContext, useMemo, useState, useReducer } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { removeClassCode } from '../utils/class-code';

const deniedStashURLs = ['/sign-up', '/sign-in', '/sign-in-clever', '/sign-in-clever-redirect'];

/**
 * Create Site Context
 *
 * @type {React.Context<unknown>}
 */
const SiteContext = React.createContext();

export function useSite() {
  return useContext(SiteContext);
}

function cacheReducer(state, action) {
  switch (action.type) {
    case 'add':
      return { ...state, ...{ [action.key]: action.data } };
    case 'clear':
      return {};
    default:
      return {};
  }
}

export function SiteProvider({ children }) {
  const data = useStaticQuery(graphql`
    query {
      site {
        pathPrefix
      }
    }
  `);

  /**
   * @type {[String, Function]} Stashed URL for authentication redirection.
   * When a user visits a page requiring authentication and needs to be redirected back after log in or sign up.
   */
  const [stashURL, _setStashURL] = useState(null);

  /**
   * @type {[String, Function]} Flag for simplified sign up.
   * At time of creation, visitors requiring login from resources page get simplified sign up
   */
  const [signUpVariant, setSignUpVariant] = useState(null);

  /**
   * @type {[Object, Function]} Determine if page, on initial render, is the result of a user going back.
   * Specifically used on index page, where the feed is dynamically generated and scroll position must be manually retained.
   */
  const [back, setBack] = useState(false);

  /**
   * @type {[Number, Function]} Saved scroll position on index page.
   * The feed is dynamically generated and scroll position must be manually retained.
   */
  const [indexScrollPosition, setIndexScrollPosition] = useState(0);

  /**
   * @type {[Array, Function]} Cached version of homepage feed.
   * If the user returns to the homepage within the same page load, the feed should not change.
   */
  const [indexFeedCache, setIndexFeedCache] = useState([]);

  /**
   * @type {[String, Function]} Cached key of homepage feed.
   * Creates key, related to users skills, associated with feed cache. If skills change, key will not match and a new
   * cache will be built.
   */
  const [indexFeedCacheKey, setIndexFeedCacheKey] = useState(null);

  /**
   * @type {[Boolean, Function]} Cached favorite filter pressed state.
   */
  const [favoriteFilter, setFavoriteFilter] = useState(null);

  /**
   * @type {[Boolean, Function]} Potentially allow show explorer modal to be displayed
   * After explorer modal is shown, it is set as false to prevent repeatedly showing the modal during a session
   */
  const [showExplorerModal, setShowExplorerModal] = useState(true);

  /**
   * @type {[Boolean, Function]} Potentially allow show badge modal to be displayed
   * After badge modal is shown, it is set as false to prevent repeatedly showing the modal during a session
   */
  const [showBadgeModal, setShowBadgeModal] = useState(true);

  /**
   * @type {[Boolean, Function]} Potentially allow show power skills modal to be displayed
   * After modal is shown, it is set as false to prevent repeatedly showing the modal during a session
   */
  const [showPowerSkillsModal, setShowPowerSkillsModal] = useState(true);

  /**
   * @type {[Array, Function]} Queue of alerts to display
   */
  const [alerts, setAlerts] = useState([]);

  /**
   * @type {[Array]} Resource filters
   */
  const [resourceFilter, setResourceFilter] = useState([]);

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  /**
   * Set stash URL
   *
   * @param {string} url
   */
  function setStashURL(url) {
    const pathPrefix = data && data.site.pathPrefix ? data.site.pathPrefix : '';
    const re = new RegExp(`^${escapeRegExp(pathPrefix)}`);

    if (url) {
      const path = url.replace(re, '').replace(/\/+$/, '');

      if (deniedStashURLs.indexOf(path) === -1) {
        _setStashURL(url);
      }
    } else {
      _setStashURL(url);
    }
  }

  /**
   * @type {[Array]} Cache
   */
  const [cache, setCache] = useReducer(cacheReducer, {});

  /**
   * Add alert
   *
   * @param {Object} alert
   */
  function addAlert(alert) {
    setAlerts([alert, ...alerts]);
  }

  /**
   * Pop (remove) next alert in line
   *
   * @returns {undefined|Object}
   */
  function popAlert() {
    const alert = alerts[alerts.length - 1];

    if (alert) {
      const newAlerts = [...[], ...alerts];
      newAlerts.pop();
      setAlerts(newAlerts);

      return alert;
    }

    return undefined;
  }

  /**
   * Get api cache key
   *
   * @param {string} name
   * @param {Array} args
   * @return {string}
   */
  function getApiCacheKey(name, args = []) {
    return `api::${name}::${args.join('|')}`;
  }

  /**
   * Set api cache
   *
   * @param {string} key
   * @param {Object} value
   */
  function setApiCache(key, value) {
    setCache({ type: 'add', key, data: JSON.parse(JSON.stringify(value)) });
  }

  /**
   * Get api cache
   */
  function getApiCache(key) {
    return cache[key] ? JSON.parse(JSON.stringify(cache[key])) : undefined;
  }

  /**
   * Clear api cache
   */
  function clearApiCache() {
    setCache({ type: 'clear' });
  }

  /**
   * Get classrooms
   *
   * @param {Boolean} allowCache
   * @returns {Promise<Array>}
   */
  async function getClassrooms(allowCache = true) {
    const key = getApiCacheKey('getClassrooms');
    let cacheItem;

    if (allowCache) {
      cacheItem = getApiCache(key);

      if (cacheItem) {
        return cacheItem;
      }
    }

    const response = await httpsCallable(functions, 'getClassrooms')();
    setApiCache(key, response);
    return response;
  }

  /**
   * Get classroom
   *
   * @param {string} classroomId
   * @param {Boolean} allowCache
   * @returns {Promise<Array>}
   */
  async function getClassroom(classroomId, allowCache = true) {
    const key = getApiCacheKey('getClassroom', [classroomId]);
    let cacheItem;

    if (allowCache) {
      cacheItem = getApiCache(key);

      if (cacheItem) {
        return cacheItem;
      }
    }

    const response = await httpsCallable(functions, 'getClassroom')({ classroomId });
    setApiCache(key, response);
    return response;
  }

  /**
   * Get classroom user
   *
   * @param {string} classroomId
   * @param {string} classroomId
   * @param {Boolean} allowCache
   * @returns {Promise<Array>}
   */
  async function getClassroomUser(classroomId, studentId, allowCache = true) {
    const key = getApiCacheKey('getClassroomUser', [classroomId, studentId]);
    let cacheItem;

    if (allowCache) {
      cacheItem = getApiCache(key);

      if (cacheItem) {
        return cacheItem;
      }
    }

    const response = await httpsCallable(functions, 'getClassroomUser')({ classroomId, studentId });
    setApiCache(key, response);
    return response;
  }

  /**
   * Get contentful by ids
   *
   * @param {string} contentfulIds
   * @param {Boolean} allowCache
   */
  async function getContentfulByIds(contentfulIds, allowCache = true) {
    const key = getApiCacheKey('getContentfulByIds', [contentfulIds]);
    let cacheItem;

    if (allowCache) {
      cacheItem = getApiCache(key);

      if (cacheItem) {
        return cacheItem;
      }
    }

    const response = await httpsCallable(functions, 'getContentfulByIds')({ contentfulIds });
    setApiCache(key, response);
    return response;
  }

  /**
   * Create classroom
   *
   * @param {string} title
   * @param {string} subject
   */
  async function createClassroom(title, subject) {
    const response = await httpsCallable(
      functions,
      'createClassroom'
    )({
      title,
      subject,
    });

    return response;
  }

  /**
   * Edit classroom
   *
   * @param {String} id
   * @param {String} title
   * @param {String} subject
   */
  async function editClassroom(id, title, subject) {
    const response = await httpsCallable(
      functions,
      'editClassroom'
    )({
      id,
      title,
      subject,
    });

    return response;
  }

  /**
   * Join classroom
   *
   * @param {string} code
   */
  async function joinClassroom(id) {
    await httpsCallable(
      functions,
      'joinClassroom'
    )({
      id,
    });
    removeClassCode();
  }

  /**
   * Can join classroom
   *
   * @param {String} id
   */
  async function canJoinClassroom(id) {
    await httpsCallable(
      functions,
      'canJoinClassroom'
    )({
      id,
    });
  }

  /**
   * Archive classroom
   *
   * @param {String} id
   */
  async function archiveClassroom(id) {
    await httpsCallable(
      functions,
      'archiveClassroom'
    )({
      id,
    });
  }

  /**
   * Unarchive classroom
   *
   * @param {String} id
   */
  async function unarchiveClassroom(id) {
    await httpsCallable(
      functions,
      'unarchiveClassroom'
    )({
      id,
    });
  }

  /**
   * Archive classroom student
   *
   * @param {String} id
   * @param {String} student
   */
  async function archiveClassroomStudent(id, student) {
    await httpsCallable(
      functions,
      'archiveClassroomStudent'
    )({
      id,
      student,
    });
  }

  /**
   * Unarchive classroom student
   *
   * @param {String} id
   * @param {String} student
   */
  async function unarchiveClassroomStudent(id, student) {
    await httpsCallable(
      functions,
      'unarchiveClassroomStudent'
    )({
      id,
      student,
    });
  }

  /**
   * Get classroom titles
   */
  async function getClassroomTitles() {
    const response = await httpsCallable(functions, 'getClassroomTitles')();
    return response;
  }

  const value = useMemo(() => {
    return {
      stashURL,
      setStashURL,
      signUpVariant,
      setSignUpVariant,
      back,
      setBack,
      indexScrollPosition,
      setIndexScrollPosition,
      indexFeedCache,
      setIndexFeedCache,
      indexFeedCacheKey,
      setIndexFeedCacheKey,
      favoriteFilter,
      setFavoriteFilter,
      showExplorerModal,
      setShowExplorerModal,
      showBadgeModal,
      setShowBadgeModal,
      showPowerSkillsModal,
      setShowPowerSkillsModal,
      alerts,
      popAlert,
      addAlert,
      pathPrefix: data && data.site.pathPrefix ? data.site.pathPrefix : '',
      setResourceFilter,
      resourceFilter,
      clearApiCache,
      getClassrooms,
      getClassroom,
      getClassroomUser,
      getContentfulByIds,
      createClassroom,
      editClassroom,
      joinClassroom,
      archiveClassroom,
      unarchiveClassroom,
      archiveClassroomStudent,
      unarchiveClassroomStudent,
      canJoinClassroom,
      getClassroomTitles,
    };
  }, [
    stashURL,
    signUpVariant,
    back,
    cache,
    indexScrollPosition,
    indexFeedCache,
    indexFeedCacheKey,
    favoriteFilter,
    showExplorerModal,
    showBadgeModal,
    alerts,
    data,
    setResourceFilter,
    resourceFilter,
    showPowerSkillsModal,
  ]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

SiteProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
